import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, X, Calendar, DollarSign, FolderOpen } from 'lucide-react';
import { dashboardService } from '../../services/dashboardService';

const ProjectionsWidget = ({ viewType, userId }) => {
  const navigate = useNavigate();
  const [projections, setProjections] = useState(null);
  const [trustData, setTrustData] = useState({ month: null, quarter: null });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalPeriod, setModalPeriod] = useState('quarter');
  const [detailProjects, setDetailProjects] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const fetchProjections = useCallback(async () => {
    setLoading(true);
    try {
      const [projRes, trustMonthRes, trustQuarterRes] = await Promise.all([
        dashboardService.getProjections(viewType),
        dashboardService.getProjectionsTrust(viewType, 'month').catch(() => ({ data: null })),
        dashboardService.getProjectionsTrust(viewType, 'quarter').catch(() => ({ data: null })),
      ]);
      setProjections(projRes.data);
      setTrustData({
        month: trustMonthRes.data,
        quarter: trustQuarterRes.data,
      });
    } catch (error) {
      console.error('Error fetching projections:', error);
      setProjections(null);
    } finally {
      setLoading(false);
    }
  }, [viewType]);

  useEffect(() => {
    fetchProjections();
  }, [fetchProjections]);

  const fetchDetails = useCallback(async (period) => {
    setLoadingDetails(true);
    try {
      const response = await dashboardService.getProjectionsDetails(viewType, period);
      setDetailProjects(response.data.projects || []);
    } catch (error) {
      console.error('Error fetching projection details:', error);
      setDetailProjects([]);
    } finally {
      setLoadingDetails(false);
    }
  }, [viewType]);

  const handleCardClick = (period) => {
    setModalPeriod(period);
    setShowModal(true);
    fetchDetails(period);
  };

  const handleProjectClick = (projectId) => {
    setShowModal(false);
    navigate(`/projects/${projectId}`);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'planned':
        return 'bg-gray-100 text-gray-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'text-red-600';
      case 'high':
        return 'text-orange-600';
      case 'medium':
        return 'text-yellow-600';
      case 'low':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  // Compute collectible total from trust data (sum of per-case collectible)
  const getCollectible = (period) => {
    const td = period === 'month' ? trustData.month : trustData.quarter;
    if (!td || !td.cases) return null;
    return td.cases.reduce((sum, c) => sum + (c.collectible || 0), 0);
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl shadow-lg border border-green-200 transition-all duration-300 hover:shadow-2xl hover:shadow-green-100 hover:-translate-y-1">
        <div className="animate-pulse">
          <div className="flex items-center mb-4">
            <div className="h-12 w-12 bg-green-200 rounded-xl"></div>
            <div className="ml-4 flex-1">
              <div className="h-4 bg-green-200 rounded w-24 mb-2"></div>
              <div className="h-8 bg-green-200 rounded w-32"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!projections) {
    return (
      <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl shadow-lg border border-green-200">
        <div className="flex items-center">
          <div className="p-3 rounded-xl bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg">
            <Target className="h-6 w-6" />
          </div>
          <div className="ml-4">
            <p className="text-sm font-semibold text-gray-700 mb-1">Projections</p>
            <p className="text-gray-500">Unable to load data</p>
          </div>
        </div>
      </div>
    );
  }

  const monthCollectible = getCollectible('month');
  const quarterCollectible = getCollectible('quarter');

  return (
    <>
      <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl shadow-lg border border-green-200 transition-all duration-300 hover:shadow-2xl hover:shadow-green-100 hover:-translate-y-1 group">
        <div className="flex items-center mb-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg group-hover:scale-110 transition-transform duration-300">
            <Target className="h-6 w-6" />
          </div>
          <div className="ml-4">
            <p className="text-sm font-semibold text-gray-700">
              {viewType === 'my' ? 'My Projections' : 'Firm Projections'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* This Month */}
          <div
            onClick={() => handleCardClick('month')}
            className="bg-white/60 rounded-lg p-3 cursor-pointer hover:bg-white/80 transition-colors"
          >
            <p className="text-xs text-gray-600 font-medium mb-1">This Month</p>
            <p className="text-lg font-bold text-gray-900">
              {formatCurrency(projections.this_month.estimated_value)}
            </p>
            <p className="text-[10px] text-gray-500 uppercase tracking-wide">Projected</p>
            {monthCollectible !== null && (
              <>
                <p className="text-lg font-bold text-green-700 mt-1">
                  {formatCurrency(monthCollectible)}
                </p>
                <p className="text-[10px] text-gray-500 uppercase tracking-wide">Collectible</p>
                {projections.this_month.estimated_value > 0 && monthCollectible < projections.this_month.estimated_value && (
                  <p className="text-[10px] text-red-500 font-medium mt-1">
                    {formatCurrency(projections.this_month.estimated_value - monthCollectible)} gap
                  </p>
                )}
              </>
            )}
            <p className="text-xs text-gray-400 mt-1">
              {projections.this_month.project_count} project{projections.this_month.project_count !== 1 ? 's' : ''}
            </p>
          </div>

          {/* This Quarter */}
          <div
            onClick={() => handleCardClick('quarter')}
            className="bg-white/60 rounded-lg p-3 cursor-pointer hover:bg-white/80 transition-colors"
          >
            <p className="text-xs text-gray-600 font-medium mb-1">This Quarter</p>
            <p className="text-lg font-bold text-gray-900">
              {formatCurrency(projections.this_quarter.estimated_value)}
            </p>
            <p className="text-[10px] text-gray-500 uppercase tracking-wide">Projected</p>
            {quarterCollectible !== null && (
              <>
                <p className="text-lg font-bold text-green-700 mt-1">
                  {formatCurrency(quarterCollectible)}
                </p>
                <p className="text-[10px] text-gray-500 uppercase tracking-wide">Collectible</p>
                {projections.this_quarter.estimated_value > 0 && quarterCollectible < projections.this_quarter.estimated_value && (
                  <p className="text-[10px] text-red-500 font-medium mt-1">
                    {formatCurrency(projections.this_quarter.estimated_value - quarterCollectible)} gap
                  </p>
                )}
              </>
            )}
            <p className="text-xs text-gray-400 mt-1">
              {projections.this_quarter.project_count} project{projections.this_quarter.project_count !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        <p className="text-xs font-medium text-gray-600 mt-3 flex items-center">
          <span className="w-2 h-2 rounded-full bg-green-500 mr-1.5 animate-pulse"></span>
          Projected vs trust-backed collectible
        </p>
      </div>

      {/* Detail Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-green-50 to-green-100 p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  {modalPeriod === 'month' ? 'This Month' : 'This Quarter'} Projections
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {detailProjects.length} project{detailProjects.length !== 1 ? 's' : ''} due to complete
                </p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700 transition-colors p-2 hover:bg-gray-200 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-100px)]">
              {loadingDetails ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
                </div>
              ) : detailProjects.length === 0 ? (
                <div className="text-center py-12">
                  <FolderOpen className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-600">No projects due this {modalPeriod}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {detailProjects.map((project) => (
                    <div
                      key={project.id}
                      onClick={() => handleProjectClick(project.id)}
                      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 hover:border-green-300 cursor-pointer transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900">{project.name}</h3>
                          <p className="text-sm text-gray-600 mt-1">
                            {project.case_number} - {project.case_title}
                          </p>
                        </div>
                        <div className="text-right ml-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(project.status)}`}>
                            {project.status.replace('_', ' ')}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-3 text-sm">
                        <div className="flex items-center space-x-4">
                          {project.due_date && (
                            <span className="flex items-center text-gray-600">
                              <Calendar className="h-4 w-4 mr-1" />
                              {new Date(project.due_date).toLocaleDateString()}
                            </span>
                          )}
                          {project.priority && (
                            <span className={`font-medium ${getPriorityColor(project.priority)}`}>
                              {project.priority.toUpperCase()}
                            </span>
                          )}
                        </div>
                        <span className="flex items-center text-green-600 font-medium">
                          <DollarSign className="h-4 w-4" />
                          {formatCurrency(project.estimated_value)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProjectionsWidget;
